"use client";

import { Link, usePathname } from "@/i18n/navigation";
import {
	NavigationMenu,
	NavigationMenuContent,
	NavigationMenuItem,
	NavigationMenuLink,
	NavigationMenuList,
	NavigationMenuTrigger,
	navigationMenuTriggerStyle
} from "@/components/ui/navigation-menu";
import { CustomButton } from "@/components/ui/custom-button";
import { useTranslations } from "next-intl";
import { MobileNav } from "./MobileNav";

const nav_menu_link_class_name =
	"block px-2 py-1 rounded-md leading-none no-underline outline-hidden transition-colors hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2";

export default function PublicNavbar({ is_signed_in = false }: { is_signed_in?: boolean }) {
	const pathname = usePathname();
	const t = useTranslations();
	const logo_href = is_signed_in ? "/app" : "/";
	const primary_href = is_signed_in ? "/app" : "/login";
	const primary_label = is_signed_in ? "Open app" : "Login";

	if (pathname.includes("/book-a-demo")) return null;

	return (
		<nav
			aria-label="Main navigation"
			className="top-0 left-0 right-0 z-60 bg-white px-2 transition-opacity duration-200 sm:px-4 md:px-[10px]"
		>
			<div className="z-40 mx-auto flex h-[60px] flex-col justify-center py-1">
				<div className="relative flex flex-row items-center justify-between px-4 lg:pr-1">
					<div className="flex shrink-0 items-center gap-2">
						<Link href={logo_href}>
							<div className="custom-hover-div z-40 flex flex-row items-center justify-start">
								<img
									src="/StudyStash - Logo_Colour - RGB.png"
									alt="StudyStash Brand Mark"
									width={180}
									className="h-auto w-[120px] cursor-pointer object-contain pt-1 xs:w-[140px] sm:w-[180px]"
								/>
							</div>
						</Link>
					</div>

					<div className="flex min-w-0 shrink-0 items-center gap-1 lg:gap-4">
						<div className="flex items-center">
							<NavigationMenu className="hidden md:flex md:flex-row">
								<NavigationMenuList className="flex gap-1 sm:gap-2 md:gap-3 lg:gap-4">
									<NavigationMenuItem className="list-none">
										<NavigationMenuTrigger className="px-2 sm:px-1">Who we help</NavigationMenuTrigger>
										<NavigationMenuContent>
											<div className="z-40 w-[300px] rounded-3xl bg-white px-4 py-3">
												<div className="space-y-1">
													<NavigationMenuLink asChild>
														<Link href="/who-we-help/universities" className={nav_menu_link_class_name}>
															<h6>Universities</h6>
														</Link>
													</NavigationMenuLink>
													<NavigationMenuLink asChild>
														<Link href="/who-we-help/fe-colleges" className={nav_menu_link_class_name}>
															<h6>{t("navbar.fe-colleges")}</h6>
														</Link>
													</NavigationMenuLink>
													<NavigationMenuLink asChild>
														<Link href="/who-we-help/students" className={nav_menu_link_class_name}>
															<h6>Students</h6>
														</Link>
													</NavigationMenuLink>
													<NavigationMenuLink asChild>
														<Link href="/who-we-help/lecturers" className={nav_menu_link_class_name}>
															<h6>{t("general.words.lecturers")}</h6>
														</Link>
													</NavigationMenuLink>
												</div>
											</div>
										</NavigationMenuContent>
									</NavigationMenuItem>

									<NavigationMenuItem className="list-none">
										<NavigationMenuTrigger className="px-2 sm:px-1">How we help</NavigationMenuTrigger>
										<NavigationMenuContent>
											<div className="z-40 w-[300px] rounded-3xl bg-white px-4 py-3">
												<div className="space-y-1">
													<NavigationMenuLink asChild>
														<Link href="/how-we-help/ai-with-guardrails" className={nav_menu_link_class_name}>
															<h6>AI with Guardrails</h6>
														</Link>
													</NavigationMenuLink>
													<NavigationMenuLink asChild>
														<Link href="/how-we-help/neuroscience" className={nav_menu_link_class_name}>
															<h6>Neuroscience</h6>
														</Link>
													</NavigationMenuLink>
													<NavigationMenuLink asChild>
														<Link href="/how-we-help/adaptive-learning" className={nav_menu_link_class_name}>
															<h6>Adaptive Learning</h6>
														</Link>
													</NavigationMenuLink>
													<NavigationMenuLink asChild>
														<Link href="/how-we-help/reduced-workload" className={nav_menu_link_class_name}>
															<h6>Reduced Workload</h6>
														</Link>
													</NavigationMenuLink>
													<NavigationMenuLink asChild>
														<Link href="/how-we-help/learner-insights" className={nav_menu_link_class_name}>
															<h6>Learner Insights</h6>
														</Link>
													</NavigationMenuLink>
												</div>
											</div>
										</NavigationMenuContent>
									</NavigationMenuItem>

									<NavigationMenuItem className="list-none">
										<NavigationMenuTrigger className="px-2 sm:px-1">About</NavigationMenuTrigger>
										<NavigationMenuContent>
											<div className="z-40 w-[300px] rounded-3xl bg-white px-4 py-3">
												<div className="space-y-1">
													<NavigationMenuLink asChild>
														<Link href="/about/our-story" className={nav_menu_link_class_name}>
															<h6>Our Story</h6>
														</Link>
													</NavigationMenuLink>
													<NavigationMenuLink asChild>
														<Link href="/advisory-board" className={nav_menu_link_class_name}>
															<h6>Advisory Board</h6>
														</Link>
													</NavigationMenuLink>
													<NavigationMenuLink asChild>
														<Link href="/about/case-studies" className={nav_menu_link_class_name}>
															<h6>News and Insights</h6>
														</Link>
													</NavigationMenuLink>
													<NavigationMenuLink asChild>
														<Link href="/about/events-webinars" className={nav_menu_link_class_name}>
															<h6>Events and Webinars</h6>
														</Link>
													</NavigationMenuLink>
													<NavigationMenuLink asChild>
														<Link
															href="https://trust.studystash.com"
															target="_blank"
															rel="noopener noreferrer"
															className={nav_menu_link_class_name}
														>
															<h6>Trust and Safety Hub</h6>
														</Link>
													</NavigationMenuLink>
													<NavigationMenuLink asChild>
														<Link
															href="https://internal.support.studystash.com/servicedesk/customer/portals"
															className={nav_menu_link_class_name}
														>
															<h6>{t("navbar.help-centre")}</h6>
														</Link>
													</NavigationMenuLink>
												</div>
											</div>
										</NavigationMenuContent>
									</NavigationMenuItem>
								</NavigationMenuList>
							</NavigationMenu>

							<div className="pr-2 md:pr-0">
								<MobileNav />
							</div>

							<div className="ml-0 mr-0 hidden h-10 w-px bg-green-400 sm:ml-4 md:ml-6 lg:ml-8 lg:mr-4 lg:block" />

							{!is_signed_in ? (
								<Link href="/register" className={`${navigationMenuTriggerStyle()} hidden sm:inline-flex`}>
									Sign up
								</Link>
							) : null}

							<Link href={primary_href}>
								<CustomButton
									variant="primary"
									size="lg"
									className="px-3 py-1.5 text-xs sm:px-5 sm:py-2 sm:text-sm md:px-8 md:py-3 md:text-base"
								>
									{primary_label}
								</CustomButton>
							</Link>
						</div>
					</div>
				</div>
			</div>
		</nav>
	);
}
